import { useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { showToast } from '@/contexts/ToastContext';
import { hasStringValue } from '@/utils/validation';
import { getAppOrBrowserString } from '@/utils/browser';
import { fetchBackendVersion } from '@/services/version-service';
import { reloadPageSkipBrowserCache } from '@/utils/dom';
import { BaseFormModal } from '@/components/BaseFormModal';

export function LoginScreen() {
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [loginError, setLoginError] = useState<ReactNode>('');

  // Forgot password modal
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');

  const submitForgotPassword = useCallback(async () => {
    const username = (forgotPasswordEmail || '').trim();
    if (!hasStringValue(username)) {
      showToast('Email required.', 'error');
      return;
    }
    try {
      await api.passwordResetByUsername({ username });
      setForgotPasswordOpen(false);
      setForgotPasswordEmail('');
      showToast('Email Sent', 'success');
    } catch {
      showToast('Error sending email.', 'error');
    }
  }, [forgotPasswordEmail]);

  const submitSignIn = useCallback(async () => {
    setSigningIn(true);
    setLoginError('');

    if (!loginUsername || !loginPassword) {
      setSigningIn(false);
      return;
    }

    if (!loginUsername.includes('@')) {
      setLoginError(
        <>
          Please enter an email address for the client portal. Looking for Orna?{' '}
          <a href="https://orna.kerkton.com/" target="_blank" rel="noreferrer">Go to orna.kerkton.com</a>
        </>
      );
      setSigningIn(false);
      return;
    }

    try {
      const result = await api.login(loginUsername, loginPassword);
      const destination = await signIn(result);
      setSigningIn(false);
      if (destination === 'home') {
        navigate('/home', { replace: true });
      } else {
        navigate('/company-select', { replace: true });
      }
    } catch (errorResult: any) {
      if (errorResult?.status === 403) {
        // Try with updated backend version
        try {
          const latestVersion = await fetchBackendVersion();
          const currentVersion = api.getCurrentBackendVersion()!;
          if (currentVersion > 0 && latestVersion > 0 && currentVersion !== latestVersion) {
            api.setCurrentBackendVersion(latestVersion);
            try {
              const retryResult = await api.login(loginUsername, loginPassword);
              const destination = await signIn(retryResult);
              setSigningIn(false);
              if (destination === 'home') {
                navigate('/home', { replace: true });
              } else {
                navigate('/company-select', { replace: true });
              }
              setTimeout(() => reloadPageSkipBrowserCache(), 0);
              return;
            } catch (innerError: any) {
              api.setCurrentBackendVersion(currentVersion);
              if (innerError?.status === 403) {
                setLoginError(`There was an issue connecting to Orna. Please close and reopen your ${getAppOrBrowserString()}, and try again.`);
              } else {
                setLoginError('Username or password is incorrect. Please try again.');
              }
            }
          } else {
            setLoginError(`There was an issue connecting to Orna. Please close and reopen your ${getAppOrBrowserString()}, and try again.`);
          }
        } catch {
          setLoginError(`There was an issue connecting to Orna. Please close and reopen your ${getAppOrBrowserString()}, and try again.`);
        }
      } else {
        setLoginError('Username or password is incorrect. Please try again.');
      }
      setSigningIn(false);
    }
  }, [loginUsername, loginPassword, signIn, navigate]);

  return (
    <>
      <BaseFormModal
        open={forgotPasswordOpen}
        title="Forgot Password?"
        saveText="Reset Password"
        onSave={submitForgotPassword}
        onCancel={() => setForgotPasswordOpen(false)}
      >
        <div className="wrap-input100 validate-input">
          <span className="label-input100">Email</span>
          <input
            className="input100"
            type="text"
            placeholder="Email"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
          />
          <span className="focus-input100"></span>
        </div>
      </BaseFormModal>

      <div id="divContainer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <div className="kstc-row">
          <section id="SectionSignIn" className="form-signin">
            <div className="limiter">
              <div className="container-login100">
                <div className="login100-more" style={{ backgroundImage: "url('/Content/Customize/img/login-2-v1.jpg')", zIndex: 'auto' }}></div>
                <div className="waveWrapper waveAnimation">
                  <div className="waveWrapperInner bgTop">
                    <div className="wave waveTop" style={{ backgroundImage: "url('/Content/Customize/img/Wave-top.png')" }}></div>
                  </div>
                  <div className="waveWrapperInner bgMiddle">
                    <div className="wave waveMiddle" style={{ backgroundImage: "url('/Content/Customize/img/Wave-mid.png')" }}></div>
                  </div>
                  <div className="waveWrapperInner bgBottom">
                    <div className="wave waveBottom" style={{ backgroundImage: "url('/Content/Customize/img/wave-bot.png')" }}></div>
                  </div>
                </div>
                <div className="wrap-login100">
                  <form className="login100-form validate-form p-l-50 p-r-50 p-t-72" onSubmit={(e) => { e.preventDefault(); submitSignIn(); }}>
                    <span className="login100-form-title p-b-59">Sign In</span>

                    {loginError && (
                      <div className="kstc-alert kstc-alert--danger">{loginError}</div>
                    )}

                    <div className="wrap-input100 validate-input" data-validate="Email is required">
                      <span className="label-input100">Email</span>
                      <input
                        className="input100"
                        type="text"
                        placeholder="Email"
                        value={loginUsername}
                        onChange={(e) => setLoginUsername(e.target.value)}
                        autoComplete="username"
                        required
                      />
                      <span className="focus-input100"></span>
                    </div>

                    <div className="wrap-input100 validate-input" data-validate="Password is required">
                      <span className="label-input100">Password</span>
                      <input
                        className="input100"
                        type="password"
                        placeholder="**********"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <span className="focus-input100"></span>
                    </div>

                    <button
                      type="submit"
                      disabled={signingIn}
                      className="kstc-btn kstc-btn--primary kstc-btn--lg"
                      style={{ width: '100%' }}
                    >
                      {!signingIn ? (
                        <span>Sign In</span>
                      ) : (
                        <span><span className="kstc-spinner kstc-spinner--sm"></span> Signing In...</span>
                      )}
                    </button>
                  </form>
                  <div className="p-l-50 p-r-50" style={{ marginTop: '5px' }}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setForgotPasswordOpen(true); }}>Forgot Password?</a>
                  </div>
                  <footer className="login-footer">
                    <a target="_blank" href="https://www.kerkton.com" rel="noreferrer">Powered by Orna</a> | <a target="_blank" href="//kerkton.com/terms" rel="noreferrer">Terms &amp; Conditions</a> | <a target="_blank" href="//kerkton.com/privacy" rel="noreferrer">Privacy Policy</a>
                  </footer>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
